import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { Mail, ArrowRight, AlertCircle, CheckCircle, Zap, ShieldQuestion } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axiosInstance.post('/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-100">
            <ShieldQuestion className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight italic mb-2 uppercase">
            AI Job Mate
          </h1>
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200 animate-pulse" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Operational</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm group hover:border-blue-200 transition-all overflow-hidden relative">
          <div className="absolute top-0 right-0 p-8 opacity-5 -rotate-12 pointer-events-none group-hover:rotate-0 transition-transform duration-1000">
            <Zap size={120} />
          </div>
          
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
            <ShieldQuestion size={16} className="text-blue-500" /> Account Recovery
          </h3>

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
              <div className="space-y-1">
                <label htmlFor="email" className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all border-b-2"
                    placeholder="Enter your registered email"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 font-bold animate-in slide-in-from-top-2">
                  <AlertCircle size={18} />
                  <span className="text-xs">{error}</span>
                </div>
              )}

              <div className="pt-4 border-t border-slate-100">
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full px-8 py-3 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center justify-center gap-2 italic disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending Link...
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight size={16} className="stroke-[3]" />
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-4 relative z-10 animate-in slide-in-from-bottom-4 fade-in">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-600">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Check your email</h3>
              <p className="text-sm text-slate-500 mb-6">
                We've sent a password reset link to <span className="font-bold text-slate-700">{email}</span>.
              </p>
              <Link 
                to="/login"
                className="inline-flex px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
              >
                Back to Sign In
              </Link>
            </div>
          )}

          {/* Footer */}
          {!success && (
            <div className="mt-6 pt-6 border-t border-slate-100 text-center relative z-10">
              <p className="text-[10px] font-bold text-slate-400 italic">
                Remember your password?{' '}
                <Link to="/login" className="text-blue-600 font-black hover:text-blue-700 transition-colors uppercase tracking-widest">
                  Sign In
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
